import { GenericConfigType, ImageConfig, PortItem } from '@msc-b2c/interfaces/interfaces';
import { ItineraryResponse } from '@msc-b2c/interfaces/types';

/**
 * Image Business Logic Fallback Order
 * 1) genericConfigs.configs.cruiseSearchConfig.itineraryImages.itineraryCodesImagesConfig.{itineraryCode}
 * 2) port images from GraphQL (ports fragments)
 * 3) genericConfigs.configs.cruiseSearchConfig.itineraryImages.areaImagesConfig.{areaCode}
 * 4) genericConfigs.configs.cruiseSearchConfig.itineraryImages.fallbackImagesConfig
 */

function getPortsInOrderFromItinerary(itinData: ItineraryResponse): string[] {
  const portOfCallsWithoutSeaday =
    itinData.days
      ?.map((day) => day.port.code)
      .filter((code) => code !== 'SEADAY' && code !== 'C1C') ?? [];

  const lastPortCode = itinData.days?.[itinData.days.length - 1]?.port?.code;
  return portOfCallsWithoutSeaday.length > 0
    ? portOfCallsWithoutSeaday
    : lastPortCode
    ? [lastPortCode]
    : [];
}

export function getPortImagesFromItineraryData(
  portsData: PortItem[] | null,
  itinData: ItineraryResponse
): ImageConfig[] {
  if (!portsData?.length) return [];

  const portsInOrder = getPortsInOrderFromItinerary(itinData);

  const images: ImageConfig[] = [];

  for (const portCode of portsInOrder) {
    portsData
      .filter((item) => item.portCode === portCode && item.desktopPortImage !== null)
      .forEach((item) => {
        images.push({
          imagePath: item.desktopPortImage.imgDmPath || item.desktopPortImage.imgPath,
          imgPath: item.desktopPortImage.imgPath,
          imgDmPath: item.desktopPortImage.imgDmPath,
          altText: item.altTextPortImage,
        });
      });

    portsData
      .filter((item) => item.portCode === portCode && item.sectionImage !== null)
      .forEach((item) => {
        images.push({
          imagePath: item.sectionImage.imgDmPath || item.sectionImage.imgPath,
          imgPath: item.sectionImage.imgPath,
          imgDmPath: item.sectionImage.imgDmPath,
          altText: item.altTextSectionImage,
        });
      });
  }

  return images;
}

export function enrichImageAltFromPortImages(
  image: ImageConfig | undefined,
  portsData: PortItem[] | null | undefined,
  itinData: ItineraryResponse | undefined
): ImageConfig | undefined {
  if (!image || image.altText || !portsData?.length || !itinData) return image;

  for (const portCode of getPortsInOrderFromItinerary(itinData)) {
    const portItem = portsData.find((item) => item.portCode === portCode);
    if (!portItem) continue;

    const altText = portItem.altTextPortImage || portItem.altTextSectionImage;
    if (altText) {
      return { ...image, altText };
    }
  }

  return image;
}

export function getItineraryImage(
  itinCode: string,
  areaCode: string,
  imageAlreadyAppliedCallback: undefined | ((imagePaths: ImageConfig[]) => ImageConfig),
  genericConfigs: GenericConfigType,
  portImages?: ImageConfig[]
): ImageConfig | undefined {
  //Store all available paths
  const itineraryCodeImagesConfig: ImageConfig[] =
    genericConfigs?.configs?.cruiseSearchConfig?.itineraryImages?.itineraryCodesImagesConfig?.[
      itinCode
    ];
  const areaImagesConfig =
    genericConfigs?.configs?.cruiseSearchConfig?.itineraryImages?.areaImagesConfig?.[areaCode];
  const fallbackImagesConfig =
    genericConfigs?.configs?.cruiseSearchConfig?.itineraryImages?.fallbackImagesConfig;

  //Get the first image if array and not empty
  const firstItineraryImage = Array.isArray(itineraryCodeImagesConfig)
    ? itineraryCodeImagesConfig[0]
    : undefined;
  const firstPortImage =
    Array.isArray(portImages) && portImages.length > 0 ? portImages[0] : undefined;
  const firstAreaImage = Array.isArray(areaImagesConfig) ? areaImagesConfig[0] : undefined;
  const firstFallbackImage = Array.isArray(fallbackImagesConfig)
    ? fallbackImagesConfig[0]
    : undefined;

  //In case it is used in "VIEW ALL" I use the callback to calculate which available image to get next
  if (imageAlreadyAppliedCallback && typeof imageAlreadyAppliedCallback == 'function') {
    const images =
      (isImageArrayExisting(itineraryCodeImagesConfig) && itineraryCodeImagesConfig) ||
      (isImageArrayExisting(portImages) && portImages) ||
      (isImageArrayExisting(areaImagesConfig) && areaImagesConfig) ||
      (isImageArrayExisting(fallbackImagesConfig) && fallbackImagesConfig) ||
      [];
    return imageAlreadyAppliedCallback(Array.isArray(images) ? images : []);
  }
  //In case it is singular card used in home i get the first available image in fallback order
  return firstItineraryImage || firstPortImage || firstAreaImage || firstFallbackImage || undefined;
}

// check if image arrays from config have data configured
function isImageArrayExisting(imageArray: ImageConfig[] | undefined) {
  return !!imageArray && imageArray.length > 0;
}
